import React, { type PropsWithChildren } from 'react';
import { StyleSheet, View, type ViewProps, type ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';

// ─────────────────────────────────────────────────────────────────────────────

export const ScreenHeaderContainer = (props: Props) => {
  const { children, palette = 'brown', rounded = true, style } = props;

  const { top: safeAreaTopInset } = useSafeAreaInsets();

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerDynamicStyles = useStyle(() => {
    return { paddingTop: Math.max(safeAreaTopInset, theme.spacing['4']) };
  }, [safeAreaTopInset]);

  const containerStyles = [
    styles.container,
    rounded ? styles.roundedContainer : undefined,
    paletteStyles[palette],
    containerDynamicStyles,
    style,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return <View style={containerStyles}>{children}</View>;
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    justifyContent: 'center',
    paddingHorizontal: theme.spacing['4'],
    paddingBottom: theme.spacing['4'],
  },
  roundedContainer: {
    borderBottomLeftRadius: theme.radius.large,
    borderBottomRightRadius: theme.radius.large,
  },
});

const paletteStyles = StyleSheet.create<Record<ScreenHeaderContainerPalette, ViewStyle>>({
  brown: { backgroundColor: theme.colors.BROWN },
  spinach: { backgroundColor: theme.colors.SPINACH },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<{
  palette?: ScreenHeaderContainerPalette;
  rounded?: boolean;
  style?: ViewProps['style'];
}>;

type ScreenHeaderContainerPalette = 'brown' | 'spinach';
